import { Music, Calendar, BadgeCheck, Sparkles, Pencil, Clock } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

function formatNextReset(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function LineupCard({ artist }) {
  const effectivePrice = artist.custom_price ?? artist.price;
  const hasOverride = artist.custom_price != null && artist.custom_price !== artist.price;

  return (
    <div
      data-testid={`agent-lineup-card-${artist.id}`}
      className="bg-surface border border-[#262626] rounded-xl overflow-hidden hover:border-lime/40 transition-colors"
    >
      <div className="relative h-40 bg-[#0a0a0a]">
        {artist.image_url ? (
          <img
            src={artist.image_url}
            alt={artist.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#333]">
            <Music className="w-12 h-12" />
          </div>
        )}
        <div className="absolute top-2 left-2 flex gap-1.5">
          {artist.is_guest && (
            <Badge className="bg-amber-500/15 text-amber-300 border-0 backdrop-blur-sm">
              <Sparkles className="w-3 h-3 mr-1" /> Guest
            </Badge>
          )}
          {artist.is_verified && (
            <Badge className="bg-blue-500/15 text-blue-300 border-0 backdrop-blur-sm">
              <BadgeCheck className="w-3 h-3 mr-1" /> Verified
            </Badge>
          )}
        </div>
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-heading font-bold text-lg leading-tight line-clamp-1">
            {artist.name}
          </h3>
          <div className="text-right whitespace-nowrap">
            <p className="font-mono text-lime font-bold">${Number(effectivePrice).toFixed(2)}</p>
            {hasOverride && (
              <p className="text-[10px] text-[#A1A1AA] line-through">
                ${Number(artist.price).toFixed(2)}
              </p>
            )}
          </div>
        </div>
        {artist.genres?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {artist.genres.slice(0, 3).map((g) => (
              <span
                key={g}
                className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[#1a1a1a] text-[#A1A1AA]"
              >
                {g}
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center text-xs text-[#A1A1AA] pt-1">
          <Clock className="w-3 h-3 mr-1" />
          {artist.delivery_days || 7} day delivery
          {hasOverride && (
            <span className="ml-auto text-amber-400">Custom price</span>
          )}
        </div>
      </div>
    </div>
  );
}

export function LineupTab({ lineup, onEdit }) {
  const artists = lineup?.artists || [];

  return (
    <div className="space-y-6">
      <div className="bg-surface border border-[#262626] rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-4 h-4 text-lime" />
            <h3 className="font-heading font-bold">This Week&apos;s Lineup</h3>
            <Badge className="bg-lime/10 text-lime border-0 ml-1">
              {artists.length} {artists.length === 1 ? 'artist' : 'artists'}
            </Badge>
          </div>
          <p className="text-xs text-[#A1A1AA]">
            Resets {formatNextReset(lineup?.next_reset)} — buyers see these on Weekly Specials until then.
          </p>
        </div>
        <Button
          onClick={onEdit}
          className="btn-primary"
          data-testid="agent-edit-lineup-btn"
        >
          <Pencil className="w-4 h-4 mr-2" />
          Edit Lineup
        </Button>
      </div>

      {artists.length === 0 ? (
        <div
          data-testid="agent-lineup-empty"
          className="text-center py-16 bg-surface rounded-2xl border border-[#262626]"
        >
          <Music className="w-16 h-16 mx-auto mb-4 text-[#333]" />
          <h3 className="text-xl font-heading font-bold mb-2">No artists in your lineup yet</h3>
          <p className="text-[#A1A1AA] mb-6">
            Pick artists you&apos;re recording with this week — they&apos;ll be featured on the Weekly Specials page.
          </p>
          <Button onClick={onEdit} className="btn-primary" data-testid="agent-lineup-empty-edit-btn">
            <Pencil className="w-4 h-4 mr-2" />
            Build Your Lineup
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {artists.map((artist) => (
            <LineupCard key={artist.id} artist={artist} />
          ))}
        </div>
      )}
    </div>
  );
}
